import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Chip,
  Stack,
} from '@mui/material';
import { styled } from '@mui/material/styles';

const EmailChip = styled(Chip)(({ theme }) => ({
  margin: '2px',
  '& .MuiChip-deleteIcon': {
    color: theme.palette.error.main,
  },
}));

const EmailModalTrailblaze = ({ open, onClose, onSubmit, initialEmailConfig, trainingType }) => {
  const [emailData, setEmailData] = useState({
    stage1: { to: [], cc: [] },
  });
  const [currentToEmail, setCurrentToEmail] = useState('');
  const [currentCCEmail, setCurrentCCEmail] = useState('');

  const parseEmails = (raw) =>
    raw
      .split(/[\s,;]+/)
      .map((e) => e.trim().toLowerCase())
      .filter((e) => e.includes('@'));

  const handleKeyDown = (field, e, currentEmail, setCurrentEmail) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      const emails = parseEmails(currentEmail);
      if (emails.length > 0) {
        setEmailData((prev) => ({
          stage1: {
            ...prev.stage1,
            [field]: [...(prev.stage1[field] || []), ...emails],
          },
        }));
        setCurrentEmail('');
      }
    }
  };

  const handlePaste = (field, e, setCurrentEmail) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text');
    const emails = parseEmails(pasted);
    if (emails.length > 0) {
      setEmailData((prev) => ({
        stage1: {
          ...prev.stage1,
          [field]: [...(prev.stage1[field] || []), ...emails],
        },
      }));
      setCurrentEmail('');
    }
  };

  const handleDelete = (field, emailToDelete) => {
    setEmailData((prev) => ({
      stage1: {
        ...prev.stage1,
        [field]: prev.stage1[field].filter((e) => e !== emailToDelete),
      },
    }));
  };

  const handleSubmit = () => {
    if ((emailData.stage1.to || []).length === 0) {
      return;
    }
    onSubmit(emailData);
  };

  const handleClose = () => {
    setEmailData({ stage1: { to: [], cc: [] } });
    setCurrentToEmail('');
    setCurrentCCEmail('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Typography variant="h6" fontWeight={600}>
          Configure Intimation Email{trainingType ? ` — ${trainingType}` : ''}
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Add recipients for the training intimation notification. No prior approval is required.
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            To (Required)
          </Typography>
          <Stack direction="row" flexWrap="wrap" sx={{ mb: 1 }}>
            {(emailData.stage1.to || []).map((email) => (
              <EmailChip
                key={email}
                label={email}
                onDelete={() => handleDelete('to', email)}
                size="small"
              />
            ))}
          </Stack>
          <TextField
            fullWidth
            size="small"
            placeholder="Type email and press Enter or comma"
            value={currentToEmail}
            onChange={(e) => setCurrentToEmail(e.target.value)}
            onKeyDown={(e) => handleKeyDown('to', e, currentToEmail, setCurrentToEmail)}
            onPaste={(e) => handlePaste('to', e, setCurrentToEmail)}
          />
        </Box>

        <Box>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            CC (Optional)
          </Typography>
          <Stack direction="row" flexWrap="wrap" sx={{ mb: 1 }}>
            {(emailData.stage1.cc || []).map((email) => (
              <EmailChip
                key={email}
                label={email}
                onDelete={() => handleDelete('cc', email)}
                size="small"
              />
            ))}
          </Stack>
          <TextField
            fullWidth
            size="small"
            placeholder="Type email and press Enter or comma"
            value={currentCCEmail}
            onChange={(e) => setCurrentCCEmail(e.target.value)}
            onKeyDown={(e) => handleKeyDown('cc', e, currentCCEmail, setCurrentCCEmail)}
            onPaste={(e) => handlePaste('cc', e, setCurrentCCEmail)}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} color="inherit">
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={(emailData.stage1.to || []).length === 0}
        >
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EmailModalTrailblaze;
